import {
  AuthorizationReview,
  getRequestedMcpAccessLevel,
  type McpAccessLevel,
  scopesForMcpAccessLevel,
} from '@/components/oauth/authorization-review';
import { WorkspaceAccessPicker } from '@/components/oauth/workspace-access-picker';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useLoginRedirect } from '@/hooks/use-login-redirect';
import { type OrgEntry, useOrgPreselect } from '@/hooks/use-org-preselect';
import { API_URL } from '@/lib/api/core';
import { useAuthState } from '@/lib/auth-state';
import { getMcpResourceWorkspaceSlug } from '@/lib/oauth-workspace-access';
import { pruneSearch } from '@/lib/router-search';
import { workspaceLabel } from '@/lib/workspace-label';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type DeviceSearchParams = {
  user_code?: string;
  return_to?: string;
};

type OAuthErrorFields = {
  error?: unknown;
  error_description?: unknown;
};

type DeviceInfoResponse = OAuthErrorFields & {
  client_name?: string | null;
  client_id?: string | null;
  logo_uri?: unknown;
  client_uri?: unknown;
  scopes?: unknown;
  resource?: unknown;
  multi_workspace_grants_enabled?: unknown;
};

type DeviceApproveResponse = OAuthErrorFields & {
  status?: unknown;
  user_code?: unknown;
};

type VerifiedDeviceCode = {
  code: string;
  clientName: string | null;
  logoUri: string | null;
  clientUri: string | null;
  scopes: string[];
  resource: string | null;
  multiWorkspaceGrantsEnabled: boolean;
};

// The device code is displayed and compared uppercase, but the server echoes
// back whatever casing the caller sent, so both sides are normalized before
// they are compared.
function normalizeUserCode(value: string) {
  return value.trim().toUpperCase();
}

const DEVICE_INFO_TIMEOUT_MS = 15_000;

async function getDeviceInfo(userCode: string) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), DEVICE_INFO_TIMEOUT_MS);
  try {
    const response = await fetch(
      `${API_URL}/oauth/device/info?user_code=${encodeURIComponent(userCode)}`,
      { credentials: 'include', signal: controller.signal },
    );
    const data = (await response.json().catch(() => null)) as DeviceInfoResponse | null;
    return { response, data };
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error('Verification timed out. Check your connection and try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

// Only the two OAuth error fields are ever shown. A body that is not an error
// response (a token payload, an unrecognized status) has nothing quotable in
// it, so it falls back to fixed copy rather than leaking server internals onto
// the screen.
function oauthErrorMessage(data: OAuthErrorFields | null, fallback: string) {
  return (
    (typeof data?.error_description === 'string' && data.error_description) ||
    (typeof data?.error === 'string' && data.error) ||
    fallback
  );
}

function sanitizeReturnTo(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  try {
    const url = new URL(trimmed);
    // Native deep-link schemes the consent page may redirect back to:
    // `lobu:` (Lobu for Mac, the scheme Lobu.app registers) and
    // `lobu-ios-bridge:` (iOS bridge).
    return url.protocol === 'lobu:' || url.protocol === 'lobu-ios-bridge:' ? trimmed : undefined;
  } catch {
    return undefined;
  }
}

export const Route = createFileRoute('/oauth/device')({
  component: DeviceConsentPage,
  validateSearch: (search: Record<string, unknown>): DeviceSearchParams =>
    pruneSearch({
      user_code:
        typeof search.user_code === 'string' && search.user_code.trim().length > 0
          ? search.user_code.trim()
          : undefined,
      return_to: sanitizeReturnTo(search.return_to),
    }),
});

function DeviceConsentPage() {
  const search = Route.useSearch();
  const { session, isReady: authReady } = useAuthState();
  const sessionLoading = !authReady;
  const [userCode, setUserCode] = useState(search.user_code || '');
  const [submitState, setSubmitState] = useState<'verify' | 'approve' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The outcome carries the exact code and client it applies to, so the
  // success screen can be correlated with the CLI's poller.
  const [done, setDone] = useState<{
    outcome: 'approved' | 'denied';
    code: string;
    clientName: string | null;
    deviceHomeLabel: string | null;
  } | null>(null);
  const [verification, setVerification] = useState<VerifiedDeviceCode | null>(null);
  const [accessLevel, setAccessLevel] = useState<McpAccessLevel | null>(null);
  const [sensitiveScopesAcknowledged, setSensitiveScopesAcknowledged] = useState(false);
  // The workspace the user picked to home the device; null keeps the personal default.
  const [chosenDeviceHomeId, setChosenDeviceHomeId] = useState<string | null>(null);
  const verificationRequest = useRef(0);
  // Re-checked here, not trusted from validateSearch: TanStack Router merges a
  // route's validated search over the root's raw search, so a return_to the
  // validator dropped still arrives on `search` and would be navigated to.
  const returnTo = sanitizeReturnTo(search.return_to);
  const deviceLabel = returnTo ? 'app' : 'terminal';
  // Arriving with the code prefilled means the user followed an access link or
  // a verification_uri_complete rather than typing a code off a device — so
  // frame it as approving an access request.
  const fromLink = Boolean(search.user_code);
  const normalizedCode = normalizeUserCode(userCode);
  const activeVerification = verification?.code === normalizedCode ? verification : null;
  const isVerified = activeVerification !== null;
  const verifiedScopes = activeVerification?.scopes ?? [];
  const approvedScopes = useMemo(
    () => scopesForMcpAccessLevel(verifiedScopes, accessLevel),
    [accessLevel, verifiedScopes],
  );
  const hasMcpScopes = verifiedScopes.some((scope) => scope.startsWith('mcp:'));
  const hasDeviceWorkerScope = verifiedScopes.includes('device_worker:run');
  const hasConnectionTokenScope = verifiedScopes.includes('connections:token');
  const requiresSensitiveScopeAcknowledgement = hasDeviceWorkerScope || hasConnectionTokenScope;
  const resourceOrg = getMcpResourceWorkspaceSlug(activeVerification?.resource);
  const requiresWorkspaceAccess = isVerified && hasMcpScopes && !resourceOrg;
  const multiWorkspaceGrantsEnabled = activeVerification?.multiWorkspaceGrantsEnabled === true;
  const requiresDeviceHome = isVerified && hasDeviceWorkerScope && !resourceOrg;
  const {
    orgList,
    workspaceAccess,
    setWorkspaceAccess,
    selectedOrgIds,
    toggleSelectedOrgId,
    resetWorkspaceSelection,
    deviceHomeOrgId,
    orgsLoading,
    orgsError,
    retryOrgs,
    workspaceSelectionReady,
  } = useOrgPreselect({
    requireDeviceHome: requiresDeviceHome,
    deviceHomeOrgId: chosenDeviceHomeId,
    allowMultiple: multiWorkspaceGrantsEnabled,
  });
  const deviceHomeOrg = orgList.find((org) => org.id === deviceHomeOrgId) ?? null;
  const organizationResolutionReady = requiresWorkspaceAccess
    ? workspaceSelectionReady
    : !requiresDeviceHome || (!orgsLoading && !orgsError && deviceHomeOrg !== null);

  const verifyDeviceCode = useCallback(async (code: string) => {
    const requestId = ++verificationRequest.current;
    setSubmitState('verify');
    setError(null);
    setSensitiveScopesAcknowledged(false);

    try {
      const { response, data } = await getDeviceInfo(code);
      if (requestId !== verificationRequest.current) return;
      if (response.status === 401) {
        const loginUrl = new URL('/auth/login', window.location.origin);
        loginUrl.searchParams.set('callbackUrl', window.location.href);
        window.location.href = loginUrl.toString();
        return;
      }
      if (!response.ok || !data) {
        throw new Error(oauthErrorMessage(data, 'Failed to verify device authorization code'));
      }
      const scopes = Array.isArray(data.scopes) ? (data.scopes as string[]) : [];
      setVerification({
        code,
        clientName: data.client_name ?? null,
        logoUri: typeof data.logo_uri === 'string' ? data.logo_uri : null,
        clientUri: typeof data.client_uri === 'string' ? data.client_uri : null,
        scopes,
        resource: typeof data.resource === 'string' ? data.resource : null,
        multiWorkspaceGrantsEnabled: data.multi_workspace_grants_enabled === true,
      });
      setAccessLevel(getRequestedMcpAccessLevel(scopes));
    } catch (err) {
      if (requestId !== verificationRequest.current) return;
      setVerification(null);
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      if (requestId === verificationRequest.current) setSubmitState(null);
    }
  }, []);

  useLoginRedirect(!!session, sessionLoading);

  // Linked codes can be verified immediately. Typed codes use the same helper
  // from the Continue action below.
  useEffect(() => {
    if (sessionLoading || !session || !search.user_code) return;
    void verifyDeviceCode(normalizeUserCode(search.user_code));
    return () => {
      verificationRequest.current += 1;
    };
  }, [session, sessionLoading, search.user_code, verifyDeviceCode]);

  const submitConsent = async (approved: boolean) => {
    const code = normalizedCode;
    if (!code) {
      setError(`Please enter the code shown in your ${deviceLabel}.`);
      return;
    }
    if (!isVerified) {
      setError('Verify the device code before approving or denying access.');
      return;
    }
    if (approved && requiresSensitiveScopeAcknowledgement && !sensitiveScopesAcknowledged) {
      setError('Confirm that you initiated this request and that the device code matches.');
      return;
    }
    if (approved && !organizationResolutionReady) {
      setError(
        orgsError
          ? 'Workspaces could not be loaded. Retry before approving.'
          : requiresDeviceHome && !deviceHomeOrg
            ? 'Choose a workspace for this device before approving.'
            : 'Choose at least one workspace before approving.',
      );
      return;
    }

    // The client name is read before the await: `activeVerification` is derived
    // from state that a code edit can clear while the request is in flight, and
    // the success screen must name the request this decision was made against.
    const decidedClientName = activeVerification?.clientName ?? null;
    const decidedHomeLabel =
      requiresDeviceHome && deviceHomeOrg ? workspaceLabel(deviceHomeOrg) : null;

    setSubmitState(approved ? 'approve' : 'deny');
    setError(null);

    try {
      const response = await fetch(`${API_URL}/oauth/device/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_code: code,
          approved,
          ...(approved ? { scope: approvedScopes.join(' ') } : {}),
          ...(approved && requiresWorkspaceAccess
            ? {
                workspace_access: workspaceAccess,
                organization_ids: selectedOrgIds,
              }
            : {}),
          // The device home. The server homes the device here and requires the
          // caller to be a member; the MCP grant above already includes it.
          ...(approved && requiresDeviceHome && deviceHomeOrg
            ? { organization_id: deviceHomeOrg.id }
            : {}),
        }),
      });

      const data = (await response.json().catch(() => null)) as DeviceApproveResponse | null;

      if (response.status === 401) {
        const loginUrl = new URL('/auth/login', window.location.origin);
        loginUrl.searchParams.set('callbackUrl', window.location.href);
        window.location.href = loginUrl.toString();
        return;
      }

      if (!response.ok) {
        throw new Error(oauthErrorMessage(data, 'Failed to process device authorization'));
      }

      // A 2xx is NOT proof the device row reached its terminal state: issue
      // #3623 saw this page render "Access Authorized" while the CLI kept
      // polling `authorization_pending`. Only the exact expected status body
      // is success; anything else (empty, `{error}`, still-pending, a status
      // we do not recognize) keeps the code on screen and reports the real
      // state, so the verifier retries instead of walking away.
      const expectedStatus = approved ? 'approved' : 'denied';
      if (data?.status !== expectedStatus) {
        throw new Error(
          oauthErrorMessage(
            data,
            approved
              ? 'The server did not confirm this approval. The device is still waiting — try again.'
              : 'The server did not confirm this denial. Try again.'
          )
        );
      }
      // The paired server change has the approve route echo back the code it
      // wrote, verbatim as the caller sent it; the deny route returns no code.
      // So the check is conditional: when a code is echoed, compare it
      // normalized — a casing difference is the same request, a different code
      // is not — and when none is echoed, the status alone stands.
      const confirmedCode =
        typeof data.user_code === 'string' ? normalizeUserCode(data.user_code) : null;
      if (confirmedCode !== null && confirmedCode !== code) {
        throw new Error(
          'The server confirmed a different device code. Re-check the code shown on your device.'
        );
      }

      setSubmitState(null);
      setDone({
        outcome: expectedStatus,
        code,
        clientName: decidedClientName,
        deviceHomeLabel: decidedHomeLabel,
      });
    } catch (err) {
      setSubmitState(null);
      setError(err instanceof Error ? err.message : 'Unexpected error');
    }
  };

  useEffect(() => {
    if (done?.outcome !== 'approved' || !returnTo) return;
    const timeout = window.setTimeout(() => {
      window.location.href = returnTo;
    }, 600);
    return () => window.clearTimeout(timeout);
  }, [done, returnTo]);

  if (sessionLoading || !session) {
    return (
      <main className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center p-4">
        <p className="text-sm text-muted-foreground">Checking authentication...</p>
      </main>
    );
  }

  if (done) {
    return (
      <main className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center p-4">
        {/* The decision is announced, not just painted: this view replaces the
            form outright, so a screen reader would otherwise get no notice that
            the outcome arrived. */}
        <Card className="w-full max-w-lg" role="status" aria-live="polite">
          <CardHeader>
            <CardTitle>
              {done.outcome === 'approved'
                ? fromLink
                  ? 'Access Authorized'
                  : 'Device Authorized'
                : 'Authorization Denied'}
            </CardTitle>
            <CardDescription>
              {done.outcome === 'approved'
                ? returnTo
                  ? 'You can close this tab and return to the Lobu app.'
                  : 'You can close this tab and return to your terminal.'
                : 'The device was not authorized. You can close this tab.'}
            </CardDescription>
          </CardHeader>
          {done.outcome === 'approved' && done.deviceHomeLabel ? (
            <CardContent className="text-sm text-muted-foreground">
              This device now lives in {done.deviceHomeLabel}. Change its workspace or share
              its connections from the device page under Connectors.
            </CardContent>
          ) : null}
          {/* Correlation evidence: name the exact code and client this decision
              applied to, so the verifier can match it against the code their
              CLI is polling instead of trusting a bare verdict. */}
          <CardContent className="text-sm">
            <dl className="space-y-1">
              <dt className="text-muted-foreground">
                {done.outcome === 'approved' ? 'Approved code' : 'Denied code'}
              </dt>
              <dd className="font-mono text-base tracking-widest text-foreground">{done.code}</dd>
              {done.clientName ? (
                <>
                  <dt className="text-muted-foreground">Requested by</dt>
                  <dd className="text-foreground">{done.clientName}</dd>
                </>
              ) : null}
            </dl>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle asChild>
            <h1>{isVerified ? 'Authorize application' : 'Connect a device'}</h1>
          </CardTitle>
          {!isVerified && (
            <CardDescription>{`Enter the code shown in your ${deviceLabel}.`}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-5" aria-busy={submitState !== null}>
          <div className="space-y-2">
            <label htmlFor="user-code" className="text-sm font-medium">
              Device code
            </label>
            <Input
              id="user-code"
              value={userCode}
              disabled={submitState === 'approve' || submitState === 'deny'}
              onChange={(e) => {
                verificationRequest.current += 1;
                setUserCode(e.target.value.toUpperCase());
                setVerification(null);
                setAccessLevel(null);
                resetWorkspaceSelection();
                setChosenDeviceHomeId(null);
                setSensitiveScopesAcknowledged(false);
                setSubmitState(null);
                setError(null);
              }}
              placeholder="ABCD-1234"
              className="font-mono text-center text-lg tracking-widest"
              maxLength={9}
              autoFocus={!search.user_code}
            />
          </div>

          {requiresDeviceHome && (
            <DeviceWorkspaceSelect
              deviceName={activeVerification?.clientName ?? null}
              orgList={orgList}
              deviceHomeOrgId={deviceHomeOrgId}
              onChange={setChosenDeviceHomeId}
              orgsLoading={orgsLoading}
              orgsError={orgsError}
              onRetry={retryOrgs}
              disabled={submitState !== null}
            />
          )}

          {activeVerification && (
            <AuthorizationReview
              idPrefix="oauth-device"
              clientName={activeVerification.clientName}
              requestedScopes={activeVerification.scopes}
              accessLevel={accessLevel}
              onAccessLevelChange={setAccessLevel}
              sensitiveScopesAcknowledged={sensitiveScopesAcknowledged}
              onSensitiveScopesAcknowledgedChange={setSensitiveScopesAcknowledged}
              disabled={submitState !== null}
              resource={activeVerification.resource}
              logoUri={activeVerification.logoUri}
              clientUri={activeVerification.clientUri}
            />
          )}

          {requiresWorkspaceAccess && (
            <WorkspaceAccessPicker
              idPrefix="oauth-device"
              allowMultiple={multiWorkspaceGrantsEnabled}
              orgList={orgList}
              selectedOrgIds={selectedOrgIds}
              onWorkspaceAccessChange={setWorkspaceAccess}
              onSelectedOrgChange={toggleSelectedOrgId}
              orgsLoading={orgsLoading}
              orgsError={orgsError}
              onRetry={retryOrgs}
              disabled={submitState !== null}
              deviceHomeOrgId={deviceHomeOrgId}
            />
          )}

          {isVerified && hasMcpScopes && resourceOrg && (
            <section className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm space-y-1">
              <p className="font-medium text-foreground">Workspace access: only {resourceOrg}</p>
              <p className="text-muted-foreground">
                This request is fixed to the workspace in the MCP resource URL.
              </p>
            </section>
          )}

          {error && (
            <p
              role="alert"
              className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-3">
            {isVerified ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => submitConsent(false)}
                  disabled={submitState !== null}
                >
                  {submitState === 'deny' ? 'Denying...' : 'Deny'}
                </Button>
                <Button
                  type="button"
                  onClick={() => submitConsent(true)}
                  disabled={
                    submitState !== null ||
                    !organizationResolutionReady ||
                    (requiresSensitiveScopeAcknowledgement && !sensitiveScopesAcknowledged)
                  }
                >
                  {submitState === 'approve' ? 'Approving...' : 'Approve'}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                onClick={() => verifyDeviceCode(normalizedCode)}
                disabled={submitState !== null || !normalizedCode}
              >
                {submitState === 'verify' ? 'Verifying...' : 'Continue'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

/**
 * Where a paired device lives. Defaults to the personal workspace; choosing a
 * team workspace homes the device there with its connections private to the
 * user until they share them.
 */
function DeviceWorkspaceSelect({
  deviceName,
  orgList,
  deviceHomeOrgId,
  onChange,
  orgsLoading,
  orgsError,
  onRetry,
  disabled,
}: {
  deviceName: string | null;
  orgList: OrgEntry[];
  deviceHomeOrgId: string | null;
  onChange: (orgId: string) => void;
  orgsLoading: boolean;
  orgsError: string | null;
  onRetry: () => void;
  disabled: boolean;
}) {
  const home = orgList.find((org) => org.id === deviceHomeOrgId) ?? null;
  return (
    <section className="space-y-2 rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
      <label htmlFor="oauth-device-home" className="block font-medium text-foreground">
        Device workspace
      </label>
      {orgsLoading ? (
        <p role="status" aria-live="polite" className="text-muted-foreground">
          Loading your workspaces...
        </p>
      ) : orgsError ? (
        <div role="alert" className="space-y-2 text-destructive">
          <p>Workspaces could not be loaded. Approval is unavailable until they load.</p>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <select
            id="oauth-device-home"
            value={home?.id ?? ''}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            {home ? null : (
              <option value="" disabled>
                Choose a workspace
              </option>
            )}
            {orgList.map((org) => (
              <option key={org.id} value={org.id}>
                {workspaceLabel(org)}
              </option>
            ))}
          </select>
          {home && !home.isPersonal ? (
            <p role="note" className="rounded-md bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-400">
              {deviceName ?? 'This device'} will be added to {home.name}. Its Shell and Browser
              connections will be private to you — teammates can't use them unless you share
              them from the device page.
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            This applies to a newly paired device. A device that is already paired keeps its
            workspace; change it from the Devices page.
          </p>
        </>
      )}
    </section>
  );
}
